import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useController, useFormContext } from 'react-hook-form';
import { ReCAPTCHA } from 'react-google-recaptcha';
import { Box, Center, Text } from '@chakra-ui/react';
import { values, isPlainObject } from 'lodash-es';
import { RECAPTCHA_TOKEN_FIELD } from '../../../../../constants/recaptchable';
import {
  registerStatus,
  registrationErrorMessageMap,
} from '../../../constants/registerStatus';
import authenticationService from '../../../../../service/authenticationService';
import pnotifyService from '../../../../../service/pnotifyService';
import { CLASSNAME } from '../../../constants/style';
import { checkIsRegistrationExisted } from '../../../utils/checkRegisterStatus';
import { getInputFieldProps } from '../../../utils/propsBuilder';
import extractServerErrorMessages from '../../../utils/extractServerErrorMessages';
import { useAppContext } from '../../../context/appContext';
import { Input, TitleText, Button, ErrorBanner } from '../../../components';

const CHECK_REGISTER_STATUS_FAILED = 'checkRegisterStatus failed';

const EmailForm = () => {
  const { t } = useTranslation();
  const {
    redirectTo,
    grecaptcha,
    isRecaptchaEnabled,
    recaptchaSiteKey,
    currentUser,
  } = useAppContext();
  const {
    errors,
    control,
    formState,
    handleSubmit,
    setError,
  } = useFormContext();
  const [errorMessages, setErrorMessages] = useState([]);
  const refUpdateUserReCAPTCHA = useRef();

  const { field: emailField } = useController({
    name: 'email',
    control,
    defaultValue: '',
  });

  const checkRegisterStatus = async () => {
    try {
      const response = await authenticationService.checkRegisterStatus({
        email: emailField.value.trim(),
      });
      const result = checkIsRegistrationExisted(response.data);

      if (result !== registerStatus.ALLOW_CREATE_MEMBER) {
        throw new Error(result);
      }
    } catch (error) {
      if (values(registerStatus).includes(error?.message)) {
        setError('email', {
          type: 'custom',
          message: t(registrationErrorMessageMap[error.message]),
        });
        throw CHECK_REGISTER_STATUS_FAILED;
      }

      // unexpected error
      throw error;
    }
  };

  const handleSubmitError = (error) => {
    if (isPlainObject(error?.response?.data)) {
      setErrorMessages(extractServerErrorMessages(error.response.data));
    } else if (error === CHECK_REGISTER_STATUS_FAILED) {
      // handle unexpected error
      console.error(error);
    }
  };

  const onSubmit = async (data) => {
    setErrorMessages([]);

    try {
      await checkRegisterStatus();

      const payload = {
        user: data,
      };
      if (isRecaptchaEnabled) {
        payload[
          RECAPTCHA_TOKEN_FIELD
        ] = await refUpdateUserReCAPTCHA.current?.executeAsync();
        refUpdateUserReCAPTCHA.current?.reset();
      }

      await authenticationService.updateUser(currentUser._id, payload);
      pnotifyService.notify(t('Member info has been updated'), {});

      window.location.href = redirectTo;
    } catch (error) {
      handleSubmitError(error);
    }
  };

  return (
    <>
      <Box mb="72px">
        <TitleText mb="16px">{t('Complete member info')}</TitleText>
        <Text
          color="#888"
          fontSize="14px"
          fontWeight={500}
          mb="32px"
          className={CLASSNAME.DESCRIPTION}
        >
          {t('Complete Email Notice')}
        </Text>
        <ErrorBanner errorMessages={errorMessages} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box mb="10px">
            <Input
              placeholder={t('Enter your email')}
              label={t('Email')}
              {...getInputFieldProps({
                field: emailField,
                error: errors?.email,
                t,
              })}
            />
          </Box>
          <Button
            isFullWidth
            onClick={handleSubmit(onSubmit)}
            mt="32px"
            type="submit"
            isLoading={formState.isSubmitting}
          >
            {t('Complete')}
          </Button>
          {isRecaptchaEnabled && (
            <ReCAPTCHA
              ref={refUpdateUserReCAPTCHA}
              size="invisible"
              grecaptcha={grecaptcha}
              sitekey={recaptchaSiteKey}
            />
          )}
        </form>
        <Center mt="64px">
          <Button
            onClick={() => (window.location.href = redirectTo)}
            variant="link"
            size="sm"
          >
            {t('Skip')}
          </Button>
        </Center>
      </Box>
    </>
  );
};

export default EmailForm;
